import { useEffect, useState, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, Timer } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { fmtDuration } from "@/lib/time";
import { EmptyState, LoadingState } from "@/components/states";
import { ManualTimeDialog } from "@/components/tasks/ManualTimeDialog";
import { DestructiveConfirm } from "@/components/dialogs";
import { cn } from "@/lib/utils";

const PERIODS = [["today", "Today"], ["week", "Week"], ["month", "Month"]];

export function MyTimeView() {
  const [period, setPeriod] = useState("today");
  const [data, setData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [del, setDel] = useState(null);

  useEffect(() => { apiFetch("/tasks?view=all").then((t) => setTasks((t || []).filter((x) => (x.time_tracking || "off") !== "off"))).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await apiFetch(`/time/entries?period=${period}&scope=me`)); }
    catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const entries = data?.entries || [];
  const byAct = data?.by_activity || {};

  return (
    <div data-testid="my-time-view">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-lg border border-border p-0.5">
          {PERIODS.map(([k, l]) => (
            <button key={k} onClick={() => setPeriod(k)} data-testid={`time-period-${k}`}
              className={cn("rounded px-3 py-1 text-xs font-medium", period === k ? "bg-secondary text-foreground" : "text-muted-foreground")}>{l}</button>
          ))}
        </div>
        <Button size="sm" className="ml-auto gap-1.5" onClick={() => { setEditEntry(null); setDlg(true); }} data-testid="add-manual-time"><Plus className="h-4 w-4" /> Add time</Button>
      </div>

      {loading ? <LoadingState label="Loading your time…" /> : (
        <>
          <div className="mb-4 grid gap-3 sm:grid-cols-3">
            <Card className="p-4"><div className="text-xs text-muted-foreground">Total tracked</div><div className="font-heading text-2xl" data-testid="time-total">{fmtDuration(data?.total_seconds)}</div></Card>
            <Card className="p-4"><div className="text-xs text-muted-foreground">Entries</div><div className="font-heading text-2xl">{entries.length}</div></Card>
            <Card className="p-4"><div className="text-xs text-muted-foreground">Activities</div><div className="font-heading text-2xl">{Object.keys(byAct).length}</div></Card>
          </div>

          {Object.keys(byAct).length > 0 && (
            <Card className="mb-4 p-4">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">By activity</div>
              <div className="space-y-1.5">
                {Object.entries(byAct).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between text-sm"><span>{k}</span><span className="text-muted-foreground">{fmtDuration(v)}</span></div>
                ))}
              </div>
            </Card>
          )}

          {entries.length === 0 ? (
            <EmptyState title="No time logged yet" message="Start a timer on a task or add time manually to see it here." action={<Button size="sm" onClick={() => setDlg(true)}>Add time</Button>} />
          ) : (
            <Card className="divide-y divide-border" data-testid="time-entries">
              {entries.map((e) => (
                <div key={e.id} className="flex items-center gap-3 px-4 py-3" data-testid={`time-entry-${e.id}`}>
                  <Timer className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm">{e.task_title || "Task"}</div>
                    <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                      <span>{e.entry_date}</span>
                      {e.activity_type && <span>{e.activity_type}</span>}
                      <span className="capitalize">{e.source}</span>
                      {e.edited && <span className="text-warning">edited</span>}
                    </div>
                  </div>
                  <span className="text-sm font-medium">{fmtDuration(e.duration_seconds)}</span>
                  <button onClick={() => { setEditEntry(e); setDlg(true); }} className="rounded p-1.5 text-muted-foreground hover:text-foreground" data-testid={`edit-entry-${e.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => setDel(e)} className="rounded p-1.5 text-muted-foreground hover:text-destructive" data-testid={`delete-entry-${e.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </Card>
          )}
        </>
      )}

      <ManualTimeDialog open={dlg} onOpenChange={setDlg} tasks={tasks} entry={editEntry} onSaved={load} />
      <DestructiveConfirm open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Delete this time entry?"
        description="This removes the logged time. This cannot be undone." confirmLabel="Delete"
        onConfirm={async () => { try { await apiFetch(`/time/entries/${del.id}`, { method: "DELETE" }); setDel(null); toast.success("Entry deleted"); load(); } catch (e) { toast.error(e.message); } }} />
    </div>
  );
}
